import { SquarePen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TooltipComponent } from "@/components/ui/tooltip";
import { Avatar } from "@/components/Common/Avatar";
import { CardListSkeleton } from "@/components/Common/SkeletonLoading";
import { useEncounter } from "@/pages/Encounters/utils/EncounterProvider";
import { formatName } from "@/Utils/utils";

import { EmptyState } from "./empty-state";

interface TruncatedTooltipProps {
  children: React.ReactNode;
  content: string;
  className?: string;
  multiline?: boolean;
}

const TruncatedTooltip = ({
  children,
  content,
  className,
  multiline = false,
}: TruncatedTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const element = ref.current;
      if (!element) return;

      // scrollWidth aur clientWidth check karne ke liye container scroll/overflow correctly calculate hona zaroori hai
      const isOverflowing = multiline
        ? element.scrollHeight > element.clientHeight
        : element.scrollWidth > element.clientWidth;

      setIsTruncated(isOverflowing);
    };

    checkTruncation();

    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);
    window.addEventListener("resize", checkTruncation);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, [content, multiline]);

  const element = (
    <span ref={ref} className={`block overflow-hidden ${className ?? ""}`}>
      {children}
    </span>
  );

  if (!isTruncated) {
    return element;
  }

  return (
    <TooltipComponent content={content} className="max-w-xs">
      {element}
    </TooltipComponent>
  );
};

export const ManageCareTeam = () => {
  const { t } = useTranslation();

  const {
    selectedEncounter: encounter,
    canWriteSelectedEncounter: canWrite,
    actions: { manageCareTeam },
  } = useEncounter();

  const [showAllMembers, setShowAllMembers] = useState(false);

  if (!encounter) {
    return <CardListSkeleton count={1} />;
  }

  const careTeam = encounter.care_team ?? [];

  const visibleMembers = showAllMembers ? careTeam : careTeam.slice(0, 3);
  const hasMoreMembers = careTeam.length > 3;
  const remainingMembers = careTeam.length - 3;

  return (
    <div className="w-full overflow-hidden rounded-md border border-gray-200 bg-gray-100 p-2 dark:border-gray-800 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-1 py-1">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-base font-semibold text-gray-950">
              {canWrite ? t("manage_care_team") : t("view_care_team")}
            </span>

            {careTeam.length > 0 && (
              <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                {careTeam.length}
              </span>
            )}
          </div>

          <p className="mt-0.5 text-xs text-gray-500">
            {careTeam.length > 0
              ? `${careTeam.length} ${t("members")}`
              : t("no_care_team")}
          </p>
        </div>

        {canWrite && (
          <TooltipComponent content={t("manage_care_team")}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 rounded-md text-gray-600 hover:bg-white hover:text-gray-950 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
              onClick={manageCareTeam}
              aria-label={t("manage_care_team")}
            >
              <SquarePen className="size-4" strokeWidth={1.5} />
            </Button>
          </TooltipComponent>
        )}
      </div>

      {/* Body */}
      <div className="rounded-md border border-gray-100 bg-white p-2 shadow-sm dark:border-gray-800 dark:bg-gray-950">
        {careTeam.length > 0 ? (
          <div className="space-y-1">
            {visibleMembers.map((member, index) => {
              const memberName = formatName(member.member);
              const avatarName = formatName(member.member, true);
              const roleDisplay = member.role?.display;

              return (
                <div
                  key={member.member.id}
                  className="flex w-full min-w-0 items-center gap-3 rounded-md px-2.5 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  {/* Avatar */}
                  <Avatar
                    name={avatarName}
                    imageUrl={member.member?.profile_picture_url}
                    className="size-10 shrink-0 rounded-full border border-white shadow-sm"
                  />

                  {/* Member Details */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {/* Member Name with Tooltip */}
                      <div className="min-w-0 flex-1">
                        <TruncatedTooltip
                          content={memberName}
                          className="truncate text-sm font-medium text-black"
                        >
                          {memberName}
                        </TruncatedTooltip>
                      </div>

                      {/* Primary Badge */}
                      {index === 0 && (
                        <Badge
                          variant="primary"
                          className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-normal"
                        >
                          {t("primary")}
                        </Badge>
                      )}
                    </div>

                    {/* Role */}
                    {roleDisplay && (
                      <TruncatedTooltip
                        content={roleDisplay}
                        multiline
                        className="mt-0.5 line-clamp-2 break-words text-xs text-gray-500"
                      >
                        {roleDisplay}
                      </TruncatedTooltip>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Show More / Less */}
            {hasMoreMembers && (
              <button
                type="button"
                onClick={() => setShowAllMembers((prev) => !prev)}
                className="mt-1 flex w-full items-center rounded-md px-2.5 py-2 text-left text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-950 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
              >
                {showAllMembers
                  ? t("show_less")
                  : `+${remainingMembers} ${t("members")}`}
              </button>
            )}
          </div>
        ) : (
          <div className="py-4">
            <EmptyState message={t("no_care_team")} />
          </div>
        )}
      </div>
    </div>
  );
};
